import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TeamCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoverStates, setHoverStates] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const autoSlideTimerRef = useRef(null);
  
  const team = [
    {
      name: "Sarah Johnson",
      role: "Chief Technology Officer",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=988&q=80",
      expertise: "AI & Machine Learning",
      description: "Pioneer in AI solutions with 15+ years of experience driving technological innovation.",
      skills: ["Neural Networks", "NLP", "Data Science"],
      color: "rgba(56, 189, 248, 0.8)"
    },
    {
      name: "Michael Chen",
      role: "Lead Developer",
      image: "https://images.unsplash.com/photo-1556157382-97eda2d62296?ixlib=rb-4.0.3&auto=format&fit=crop&w=940&q=80",
      expertise: "Full Stack Development",
      description: "Expert in scalable architecture with a proven track record in cloud solutions.",
      skills: ["React", "Node.js", "AWS"],
      color: "rgba(139, 92, 246, 0.8)"
    },
    {
      name: "Emma Rodriguez",
      role: "UX Director",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=861&q=80",
      expertise: "User Experience Design",
      description: "Award-winning designer creating intuitive and accessible interfaces.",
      skills: ["UI/UX", "Design Systems", "Prototyping"],
      color: "rgba(249, 115, 22, 0.8)"
    },
    {
      name: "David Kim",
      role: "AI Research Lead",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Deep Learning Research",
      description: "PhD in Computer Science with focus on advancing AI models for practical applications.",
      skills: ["Deep Learning", "Computer Vision", "Research"],
      color: "rgba(74, 222, 128, 0.8)"
    },
    {
      name: "Priya Sharma",
      role: "Data Science Lead",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Big Data & Analytics",
      description: "Analytics expert specializing in converting big data into actionable insights.",
      skills: ["Data Mining", "Predictive Analytics", "Python"],
      color: "rgba(236, 72, 153, 0.8)"
    },
    {
      name: "James Wilson",
      role: "Security Specialist",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Cybersecurity",
      description: "Expert in implementing robust security systems to protect data and infrastructure.",
      skills: ["Penetration Testing", "Encryption", "Security Architecture"],
      color: "rgba(251, 113, 133, 0.8)"
    },
    {
      name: "Sophia Martinez",
      role: "Cloud Architect",
      image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Cloud Infrastructure",
      description: "Designs scalable cloud solutions that optimize performance and reduce costs.",
      skills: ["AWS", "Azure", "Kubernetes"],
      color: "rgba(14, 165, 233, 0.8)"
    },
    {
      name: "Robert Taylor",
      role: "Blockchain Developer",
      image: "https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Blockchain Technology",
      description: "Creates secure, distributed ledger solutions for next-generation applications.",
      skills: ["Smart Contracts", "DeFi", "Consensus Algorithms"],
      color: "rgba(168, 85, 247, 0.8)"
    },
    {
      name: "Aisha Johnson",
      role: "Product Manager",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Product Strategy",
      description: "Transforming technical capabilities into market-ready products that users love.",
      skills: ["Agile Methodology", "User Research", "Go-to-Market"],
      color: "rgba(34, 211, 238, 0.8)"
    },
    {
      name: "Daniel Lee",
      role: "IoT Engineer",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=774&q=80",
      expertise: "Internet of Things",
      description: "Building connected device ecosystems with advanced data processing capabilities.",
      skills: ["Embedded Systems", "MQTT", "Edge Computing"],
      color: "rgba(132, 204, 22, 0.8)"
    }
  ];

  const visibleCount = Math.min(3, team.length);
  const maxIndex = Math.max(0, team.length - visibleCount);
  const AUTO_SLIDE_INTERVAL = 5000; // 5 seconds between slides

  // Initialize hover states
  useEffect(() => {
    const initialStates = {};
    team.forEach(member => {
      initialStates[member.name] = false;
    });
    setHoverStates(initialStates);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = () => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const setHoverState = (name, state) => {
    setHoverStates(prev => ({
      ...prev,
      [name]: state
    }));
  };

  // Handle automatic sliding
  useEffect(() => {
    const startAutoSlide = () => {
      clearTimeout(autoSlideTimerRef.current);
      autoSlideTimerRef.current = setTimeout(() => {
        if (!isPaused) {
          nextSlide();
        }
        startAutoSlide(); // Set up the next interval
      }, AUTO_SLIDE_INTERVAL);
    };

    startAutoSlide();

    // Clean up on component unmount
    return () => {
      clearTimeout(autoSlideTimerRef.current);
    };
  }, [isPaused, nextSlide]);

  // Animation for tech nodes
  const generateNodes = (count, radius, memberName) => {
    const nodes = [];
    const isHovered = hoverStates[memberName];
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      const size = Math.random() * 4 + 2;
      
      nodes.push(
        <motion.div
          key={`node-${memberName}-${i}`}
          className="tech-node"
          initial={{ scale: 0, x, y }}
          animate={{ 
            scale: isHovered ? 1 : 0,
            x: isHovered ? x : 0, 
            y: isHovered ? y : 0,
            opacity: isHovered ? 0.8 : 0
          }}
          transition={{ 
            duration: 0.6,
            delay: i * 0.03,
            type: "spring",
            stiffness: 200,
            damping: 20
          }}
          style={{
            width: size,
            height: size
          }}
        />
      );
    }
    return nodes;
  };

  return (
    <div 
      className="team-carousel-container"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="auto-slide-indicator">
        <div 
          className={`progress-bar ${isPaused ? 'paused' : ''}`}
          style={{ 
            animationDuration: `${AUTO_SLIDE_INTERVAL}ms`,
            animationPlayState: isPaused ? 'paused' : 'running'
          }}
        ></div>
      </div>
      
      <motion.div 
        className="team-carousel"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <AnimatePresence mode='wait'>
          {team.slice(currentIndex, currentIndex + visibleCount).map((member, index) => (
            <motion.div
              key={member.name}
              className="team-member glass-morphism"
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              animate={{ opacity: 1, y: 0, rotateY: 0 }}
              exit={{ opacity: 0, y: -30, rotateY: 15 }}
              transition={{ 
                duration: 0.7,
                delay: index * 0.15,
                ease: "easeOut"
              }}
              whileHover={{ 
                y: -15,
                transition: { duration: 0.3 }
              }}
              onHoverStart={() => {
                setHoverState(member.name, true);
                setIsPaused(true);
              }}
              onHoverEnd={() => setHoverState(member.name, false)}
              onTap={() => {
                setHoverState(member.name, !hoverStates[member.name]);
                setIsPaused(true);
              }}
            >
              <div className="member-tech-background">
                {generateNodes(24, 100, member.name)}
                <motion.div 
                  className="member-circuit-lines"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ 
                    pathLength: hoverStates[member.name] ? 1 : 0,
                    opacity: hoverStates[member.name] ? 1 : 0
                  }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                />
              </div>
              
              <motion.div 
                className="member-image-container tall-image"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 + index * 0.2 }}
              >
                <div className="image-glow" style={{ 
                  boxShadow: `0 0 30px ${member.color}`,
                  opacity: hoverStates[member.name] ? 0.7 : 0.3
                }} />
                <motion.div 
                  className="image-hexagon"
                  animate={{ 
                    rotate: hoverStates[member.name] ? 30 : 0,
                    scale: hoverStates[member.name] ? 1.05 : 1
                  }}
                  transition={{ duration: 0.5 }}
                />
                <motion.img
                  src={member.image}
                  alt={member.name}
                  className="member-image"
                  whileHover={{ scale: 1.05 }}
                />
                <motion.div 
                  className="member-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.2 }}
                />
                <motion.div 
                  className="ai-scan-effect"
                  animate={{ 
                    top: hoverStates[member.name] ? ['0%', '100%', '0%'] : '0%',
                    opacity: hoverStates[member.name] ? 1 : 0
                  }}
                  transition={{ 
                    duration: 2.4, 
                    repeat: Infinity,
                    ease: "easeInOut" 
                  }}
                />
              </motion.div>
              
              <motion.div 
                className="member-info"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.2 }}
              >
                <motion.h3
                  animate={{ 
                    textShadow: hoverStates[member.name] 
                      ? `0 0 8px ${member.color}` 
                      : '0 0 0px transparent'
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {member.name}
                </motion.h3>
                
                <motion.span className="member-role">
                  <motion.span 
                    className="role-icon"
                    animate={{ rotate: hoverStates[member.name] ? 360 : 0 }}
                    transition={{ duration: 1.2, ease: "easeInOut" }}
                  >
                    ⚙️
                  </motion.span>
                  {member.role}
                </motion.span>
                
                <motion.span 
                  className="member-expertise"
                  animate={{ 
                    backgroundColor: hoverStates[member.name] 
                      ? `rgba(56, 189, 248, 0.2)` 
                      : 'rgba(56, 189, 248, 0.1)',
                    color: hoverStates[member.name]
                      ? 'var(--primary-color)'
                      : 'var(--text-primary)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {member.expertise}
                </motion.span>
                
                <motion.p className="member-description">
                  {member.description}
                </motion.p>
                
                <motion.div 
                  className="member-skills"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ 
                    opacity: hoverStates[member.name] ? 1 : 0,
                    height: hoverStates[member.name] ? 'auto' : 0
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {member.skills.map((skill, i) => (
                    <motion.span 
                      key={skill} 
                      className="skill-badge"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ 
                        opacity: hoverStates[member.name] ? 1 : 0, 
                        x: hoverStates[member.name] ? 0 : -20 
                      }}
                      transition={{ delay: i * 0.1, duration: 0.3 }}
                    >
                      {skill}
                    </motion.span>
                  ))}
                </motion.div>
              </motion.div>
              
              <div className="tech-corner top-right"></div>
              <div className="tech-corner bottom-left"></div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      
      <div className="carousel-controls">
        <motion.button 
          className="carousel-button"
          onClick={() => {
            prevSlide();
            setIsPaused(true);
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.span 
            animate={{ x: [-2, 2, -2] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            ←
          </motion.span>
        </motion.button>
        
        <motion.div 
          className="carousel-indicators"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <motion.button
              key={index}
              className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
              onClick={() => {
                goToSlide(index);
                setIsPaused(true);
              }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              animate={{ 
                scale: currentIndex === index ? [1, 1.2, 1] : 1,
                backgroundColor: currentIndex === index ? 'var(--primary-color)' : 'var(--text-secondary)'
              }}
              transition={{ 
                duration: currentIndex === index ? 1.5 : 0.3, 
                repeat: currentIndex === index ? Infinity : 0,
                repeatType: "reverse"
              }}
            />
          ))}
        </motion.div>
        
        <motion.button 
          className="carousel-button"
          onClick={() => {
            nextSlide();
            setIsPaused(true);
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.span 
            animate={{ x: [2, -2, 2] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            →
          </motion.span>
        </motion.button>
      </div>
    </div>
  );
};

export default TeamCarousel;
